import ApiPath from "@/common/config/apiurl.config.json";
import AppConfig from "@/common/config/app.config.json";
import { V2_URL } from "@/common/config";

/**
 * Mixin untuk menangani migrasi sesi antara V1 (Legacy) dan V2 (Modern).
 * Alur migrasi menangani pemindahan token Firebase dari satu subdomain ke subdomain lain
 * menggunakan API migrasi sebagai perantara.
 */
export const migration = {
  methods: {
    /**
     * Redirect ke V2 untuk proses migrasi.
     * @param {String|Boolean} migrationId ID migrasi unik dari API (opsional).
     */
    migrationRedirectToV2(migrationId = false) {
      const url = new URL(V2_URL);
      if (migrationId) {
        url.searchParams.set("migration_id", migrationId);
      }
      window.location.href = url.toString();
    },

    /**
     * Meminta ID migrasi baru dari server.
     * Digunakan oleh pihak Requester untuk memulai proses migrasi.
     */
    async migrationRequest() {
      try {
        const { data } = await this.axios.post(ApiPath.migration.request);
        return data.data.migration_id;
      } catch (e) {
        console.error("Gagal melakukan request migrasi:", e);
        throw e;
      }
    },

    /**
     * Menyimpan token lokal ke server menggunakan ID migrasi.
     * Digunakan oleh pihak Target untuk menyediakan token bagi Requester.
     * @param {String} migrationId ID migrasi unik.
     */
    async migrationStore(migrationId) {
      const token = localStorage.getItem(AppConfig.rwStorage.customer.token);
      if (!token) return false;

      try {
        await this.axios.post(
          ApiPath.migration.store,
          {
            migration_id: migrationId,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        return true;
      } catch (e) {
        console.error("Gagal menyimpan token migrasi ke server:", e);
        return false;
      }
    },

    /**
     * Mengambil token dari server menggunakan ID migrasi.
     * Digunakan oleh pihak Requester setelah kembali dari redirect.
     * @param {String} migrationId ID migrasi unik.
     */
    async migrationRetrieve(migrationId) {
      try {
        const { data } = await this.axios.get(ApiPath.migration.retrieve, {
          params: { migration_id: migrationId },
        });
        if (data.data && data.data.token) {
          localStorage.setItem(
            AppConfig.rwStorage.customer.token,
            data.data.token
          );
          return true;
        }
        return false;
      } catch (e) {
        console.error("Gagal mengambil token migrasi dari server:", e);
        return false;
      }
    },

    /**
     * Handler utama untuk proses migrasi.
     * Mengecek URL untuk parameter migrasi atau inisialisasi migrasi jika token hilang.
     *
     * Skenario 1 & 2: Pindah Token Berhasil
     * Skenario 3 & 4: Token tidak ditemukan di kedua sisi -> Logout paksa
     *
     * @returns {Promise<Boolean>} true jika aplikasi melakukan redirect (menghentikan eksekusi selanjutnya).
     */
    async handleMigration() {
      const urlParams = new URLSearchParams(window.location.search);
      const migrationId = urlParams.get("migration_id");
      const cookies = this.cookiesGet();
      const token = localStorage.getItem(AppConfig.rwStorage.customer.token);

      if (migrationId) {
        if (token) {
          // Case: Sebagai penyediakan token
          await this.migrationStore(migrationId);
          this.migrationRedirectToV2(migrationId);
          return true;
        } else {
          // Case: Sebagai pengambil token (requester)
          const success = await this.migrationRetrieve(migrationId);

          if (!success) {
            // Gagal: Sisi lain juga tidak punya token. Hapus sesi dan balik ke V2 tanpa ID.
            this.cookiesRemove();
            this.migrationRedirectToV2();
            return true;
          }

          // Berhasil: Bersihkan URL agar tidak terjadi proses berulang
          const url = new URL(window.location.href);
          url.searchParams.delete("migration_id");
          window.history.replaceState({}, "", url.toString());
        }
      } else if (cookies && cookies.isLogin && !token) {
        // Case: Sesi aktif di cookies tapi token lokal hilang (Requester awal)
        try {
          const id = await this.migrationRequest();
          this.migrationRedirectToV2(id);
          return true;
        } catch (e) {
          console.error("Gagal menginisialisasi request migrasi:", e);
        }
      }
      return false;
    },
  },
};
